import React from 'react';
import ReactSelect, {
    ControlProps,
    GroupBase,
    MenuListProps,
    MultiValueProps,
    NonceProvider,
    Props,
    Theme as ReactSelectTheme,
    SingleValueProps,
    StylesConfig,
    components,
    SelectComponentsConfig,
    OptionProps,
} from 'react-select';
import Async, { AsyncProps } from 'react-select/async';
import { Spinner } from 'react-bootstrap';

export const SelectTheme = (theme: ReactSelectTheme) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary: '#2173DE',
        neutral40: '#6c757d',
        neutral50: '#757575',
    },
});

export function SelectMenuList<OptionType, IsMulti extends boolean = false>(
    props: MenuListProps<OptionType, IsMulti>
) {
    return <components.MenuList {...props} innerProps={{ ...props.innerProps, tabIndex: 0 }} />;
}

export function SelectLoadingIndicator() {
    return <Spinner animation="border" size="sm" role="status" className="me-2" />;
}

export type SelectProps<
    OptionType,
    IsMulti extends boolean = false,
    Group extends GroupBase<OptionType> = GroupBase<OptionType>,
> =
    | (Props<OptionType, IsMulti, Group> & {
          label: string;
          async?: undefined | false;
      })
    | (AsyncProps<OptionType, IsMulti, Group> & { label: string; async: true });

export function SelectWrapper<OptionType, IsMulti extends boolean = false>(
    props: SelectProps<OptionType, IsMulti>
) {
    if (props.async) {
        return <Async {...props} />;
    }
    return <ReactSelect {...props} />;
}

export function Select<OptionType, IsMulti extends boolean = false>({
    styles,
    ...props
}: SelectProps<OptionType, IsMulti>) {
    let comps: SelectComponentsConfig<OptionType, IsMulti, GroupBase<OptionType>> = {
        MenuList: SelectMenuList,
        LoadingIndicator: SelectLoadingIndicator,
    };
    if (props.components) {
        comps = { ...comps, ...props.components };
    }

    const customReactSelectStyles: StylesConfig<OptionType, false | true> = {
        control: (provided, { isDisabled }: ControlProps<OptionType, boolean>) => {
            return {
                ...provided,
                // keep disabled styles consistent with bootstrap form-control
                ...(isDisabled && {
                    backgroundColor: 'var(--bs-gray-200)',
                    border: '1px solid var(--bs-gray-400)',
                }),
            };
        },
        singleValue: (provided, { isDisabled }: SingleValueProps<OptionType>) => {
            return {
                ...provided,
                ...(isDisabled && {
                    color: 'var(--bs-gray-dark)',
                }),
            };
        },
        multiValue: (provided, { isDisabled }: MultiValueProps<OptionType>) => ({
            ...provided,
            // prevent new line in input box when value width is large
            maxWidth: '85%',
            ...(isDisabled && {
                color: 'var(--bs-gray-dark)',
            }),
        }),
    };

    return (
        <NonceProvider
            nonce={window.app.nonce}
            cacheKey={props.label
                .toLowerCase()
                .replaceAll(' ', '')
                .replaceAll(/[^a-z]/g, '')}
        >
            <SelectWrapper
                aria-label={props.label}
                {...props}
                theme={SelectTheme}
                components={comps}
                styles={{ ...customReactSelectStyles, ...styles }}
            />
        </NonceProvider>
    );
}

export function RemovableOption<OptionType, IsMulti extends boolean = false>(
    props: OptionProps<
        OptionType & {
            label: string;
            value: string;
            remove?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, label: string) => void;
        },
        IsMulti
    >
) {
    return (
        <div className="select-option-container">
            <components.Option {...props}>{props.children}</components.Option>
            {props.data.remove && (
                <button
                    type="button"
                    onClick={(e) => props.data.remove!(e, props.label)}
                    aria-label={`Remove ${props.label}`}
                    className="pull-right text-danger fw-bold link-text remove-option-button"
                    style={{
                        background: props.getStyles('option', props).backgroundColor as string,
                    }}
                >
                    x
                </button>
            )}
        </div>
    );
}
